import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./StudentSearchBar.module.sass";
import TextInput from "../TextInput";
import LetterAvatar from "../LetterAvatar";
import Icon from "../Icon";
import { searchStudents } from "../../services/student.service";

const StudentSearchBar = ({ 
  className,
  label = "Rechercher un étudiant",
  placeholder = "Nom, email ou téléphone (min. 2 caractères)",
  onSelectStudent,
  showDetails = true,
  required = false
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchStudents = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchStudents(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des étudiants:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    
    fetchStudents();
  }, [searchQuery]);

  // Gérer la sélection d'un étudiant
  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setSearchQuery(`${student.first_name} ${student.last_name}`);
    setShowResults(false);
    
    // Transmettre l'étudiant sélectionné au composant parent
    if (onSelectStudent) {
      onSelectStudent(student);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (selectedStudent && e.target.value !== `${selectedStudent.first_name} ${selectedStudent.last_name}`) {
      setSelectedStudent(null);
      if (onSelectStudent) {
        onSelectStudent(null);
      }
    }
  };

  // Réinitialiser la recherche
  const handleReset = () => {
    setSelectedStudent(null);
    setSearchQuery('');
    if (onSelectStudent) {
      onSelectStudent(null);
    }
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <TextInput
            className={styles.field}
            label={label}
            name="student-search"
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            required={required}
          />
          {searchQuery && (
            <button className={styles.clearButton} onClick={handleReset} type="button" aria-label="Effacer la recherche">
              <Icon name="close-circle" size="20" />
            </button>
          )}
        </div>
        
        {showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map(student => (
              <div 
                key={student.id} 
                className={styles.resultItem}
                onClick={() => handleSelectStudent(student)}
              >
                <div className={styles.resultContent}>
                  <div style={{ marginRight: '20px' }}>
                    <LetterAvatar
                      firstName={student.first_name}
                      lastName={student.last_name}
                      size={40}
                    />
                  </div>
                  <div className={styles.studentInfo}>
                    <div className={styles.studentName}>{student.first_name} {student.last_name}</div>
                    <div className={styles.studentDetails}>
                      <span>{student.email}</span>
                      <span>•</span>
                      <span>{student.phone}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !selectedStudent && (
          <div className={styles.noResults}>
            Aucun étudiant trouvé
          </div>
        )}
      </div>

      {selectedStudent && showDetails && (
        <div className={styles.selectedStudentDetails}>
          <h3>Détails de l'étudiant sélectionné</h3>
          <div className={styles.detailRow}>
            <span className={styles.label}>Nom:</span>
            <span className={styles.value}>{selectedStudent.first_name} {selectedStudent.last_name}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Email:</span>
            <span className={styles.value}>{selectedStudent.email || 'Non renseigné'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Téléphone:</span>
            <span className={styles.value}>{selectedStudent.phone || 'Non renseigné'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Date de naissance:</span>
            <span className={styles.value}>
              {selectedStudent.birth_date ? 
                new Date(selectedStudent.birth_date).toLocaleDateString('fr-FR') : 
                'Non renseignée'}
            </span>
          </div>
          {selectedStudent.type && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Type:</span>
              <span className={styles.value}>{selectedStudent.type}</span>
            </div>
          )}
          {selectedStudent.status && (
            <div className={styles.detailRow}>
              <span className={styles.label}>Statut:</span>
              <span className={styles.value}>{selectedStudent.status}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentSearchBar;
